<template>
  <div class="page-content">
    <template v-if="!isFormOpen">
      <div class="hero">
        <div
          class="hero-bg"
          :style="{ 'background-image': `url(${heroImage})` }"
        />
        <div class="hero-content-wrapper">
          <div class="hero-content">
            <div class="description__bg" />
            <div
              class="description"
              v-dompurify-html="pageData.hero.description"
            />
          </div>

          <button-blue
            v-if="isMobile"
            prevent
            type="btn-ghost-large"
            :label="pageData.formLink.text"
            @clicked="openForm"
          />
        </div>
      </div>

      <div class="content">
        <h2>{{ pageData.pageTitle }}</h2>

        <div
          v-dompurify-html="pageData.pageDescription"
          class="sub-title"
        />

        <section
          v-for="(section, key) in pageData.pageSections"
          :key="key"
          :class="key"
        >
          <div class="separator">
            <span>{{ $t(`reusable.${key}`) }}</span>
          </div>

          <item-block
            v-for="item in section"
            :key="item.title"
            :title="item.title"
            :description="item.description"
            :img="item.image"
          />
        </section>
      </div>

      <section class="consider">
        <div class="consider-content">
          <h3>{{ pageData.considerTitle }}</h3>

          <div v-dompurify-html="pageData.considerDescription" />
        </div>
        <div class="img-wrapper">
          <div class="img-container">
            <img
              :src="pageData.considerImage.source"
              :alt="pageData.considerImage.alt"
            />
          </div>
          <button-blue
            v-if="!isMobile"
            prevent
            type="btn-blue-dark"
            :label="pageData.formLink.text"
            @clicked="openForm"
          />
        </div>
      </section>

      <section class="consider">
        <div class="final-content">
          <h3>{{ pageData.notConsiderTitle }}</h3>

          <div v-dompurify-html="pageData.notConsiderDescription" />

          <button-blue
            v-if="isMobile"
            prevent
            type="btn-blue-dark"
            :label="pageData.formLink.text"
            @clicked="openForm()"
          />
        </div>
      </section>
    </template>

    <partnership-form v-if="isFormOpen" />
  </div>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'
  import ImageHelpers from 'utils/mixins/ImageHelpers'
  import ScrollUtils from 'utils/scrollUtils'
  const ButtonBlue = () =>
    import(/* webpackChunkName: "btn-blue" */ 'components/Buttons/Blue')

  import ItemBlock from 'containers/Pages/Partnership/ItemBlock'

  export default {
    name: 'PartnershipPage',
    components: {
      ButtonBlue,
      ItemBlock,
      PartnershipForm: () => import('containers/Pages/Partnership/PartnershipForm.vue')
    },
    mixins: [ImageHelpers],
    computed: {
      ...mapGetters('device', ['isMobile']),
      ...mapState('partnership', ['pageData', 'isFormOpen']),
      heroImage() {
        const { mobile, tablet, desktop } = this.pageData.hero.image

        return this.$_ImageHelpers_responsiveImage({
          desktop: desktop.source,
          tablet: tablet.source,
          mobile: mobile.source
        })
      }
    },
    methods: {
      openForm() {
        this.$store.commit('partnership/setIsFormOpen', true)
        ScrollUtils.smoothScroll(0, 300)
      }
    }
  }
</script>
<style
  lang="scss"
  scoped
>
  @import '~assets/styles/common.scss';

  .page-content {
    font-size: em(16);
    font-weight: 400;
    line-height: 24px;
    color: $blue-dark;
  }

  .hero {
    position: relative;
    margin: 0 auto;
    overflow: hidden;

    @include bp-mf-tablet {
      max-width: $tablet-wide-width;
    }

    @include bp-desktop {
      max-width: $desktop-wide-width;
    }

    &-bg {
      background-position: center;
      background-repeat: no-repeat;
      background-size: cover;
      height: 96px;
      display: flex;
      justify-content: center;
      align-items: center;

      @include bp-mf-tablet {
        height: 380px;
      }
    }

    &-content-wrapper {
      padding: 24px 16px 32px;
      margin: 0 auto;
      position: relative;

      @include bp-mf-tablet {
        align-items: center;
        display: flex;
        flex-direction: column;
        height: 384px;
        justify-content: flex-end;
        left: 0;
        margin: 0;
        padding: 0;
        position: absolute;
        top: 0;
        width: 100%;
      }

      @include bp-desktop {
        padding-left: 0;
        padding-right: 0;
      }
    }

    &-heading {
      color: white;
      font-size: em(21, 16);
      font-weight: 500;
      line-height: 32px;
      text-align: center;
      margin: 0;

      @include bp-mf-tablet {
        font-size: em(50, 16);
        line-height: 64px;
      }
    }

    .description {
      z-index: 1;
      position: relative;
      width: 100%;
      margin-bottom: 32px;

      &__bg {
        backdrop-filter: blur(4px);
        -webkit-backdrop-filter: blur(4px);
        width: 100%;
        height: 100%;
        position: absolute;
        bottom: 0;
      }

      ::v-deep p {
        margin-top: 0 !important;
      }

      @include bp-mf-tablet {
        color: white;
        width: 90%;
        max-width: 90%;
        margin: 20px auto;
        font-weight: 400;
        font-size: rem(21, 16);
        line-height: 32px;
        text-align: center;
      }

      @include bp-desktop {
        width: 100%;
        max-width: 80%;
      }
    }

    &-content {
      position: relative;
    }
  }

  .content {
    margin: 0 auto;
    padding: 10px 16px 16px;
    border-bottom: 1px solid $divider;
    margin-bottom: 30px;

    @include bp-mf-tablet {
      padding: 64px 23px;
      max-width: $tablet-wide-width;
      margin-bottom: 52px;
    }

    @include bp-desktop {
      max-width: $desktop-wide-width;
      padding: 64px 0;
    }

    h2 {
      font-size: em(21, 16);
      line-height: 28px;
      font-weight: 500;
      margin-top: 0;
      margin-bottom: 24px;

      @include bp-mf-tablet {
        font-size: em(38, 16);
        line-height: 48px;
      }
    }

    .sub-title {
      font-size: em(16, 16);
      font-weight: 400;
      line-height: 24px;
      color: $blue-dark;
      margin-bottom: 40px;

      @include bp-mf-tablet {
        font-size: em(21, 16);
        font-weight: 300;
        line-height: 32px;
        color: $blue-dark;
      }
    }

    section {
      .separator {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        margin-bottom: 16px;
        position: relative;

        @include bp-mf-tablet {
          margin-bottom: 8px;
        }

        &:before {
          content: '';
          display: block;
          height: 1px;
          width: calc(100% + 32px);
          background: $divider;
          position: absolute;
          margin-left: -16px;
          margin-right: -16px;
          left: 0;
          z-index: -1;

          @include bp-mf-tablet {
            margin: 0;
            width: 100%;
          }
        }

        span {
          display: inline-block;
          border-radius: 16px;
          padding: 5px 20px;
          background: $green-base;
          font-size: em(13, 16);
          font-weight: 500;
          line-height: 22px;
          text-transform: uppercase;
          text-align: center;
          min-width: 116px;
        }
      }
    }
  }

  .consider {
    padding: 0 16px;

    @include bp-mf-tablet {
      margin: 0 auto;
      display: flex;
      flex-direction: row;
      max-width: $tablet-wide-width;
      border-bottom: 1px solid $divider;
      justify-content: space-between;
      padding: 0 23px 32px;
      margin-bottom: 32px;
    }

    @include bp-desktop {
      max-width: $desktop-wide-width;
      padding-bottom: 64px;
      margin-bottom: 64px;
      padding-left: 0;
      padding-right: 0;
    }

    .consider-content {
      @include bp-mf-tablet {
        width: 39%;
      }
    }

    &:last-child {
      margin-bottom: 64px;

      @include bp-mf-tablet {
        margin-bottom: 0;
      }
    }

    h3 {
      margin-top: 0;
      margin-bottom: 24px;
      font-size: em(21, 16);
      font-weight: 500;
      line-height: 32px;

      @include bp-mf-tablet {
        font-size: em(28, 16);
        line-height: 36px;
        margin-bottom: 38px;
      }
    }

    ::v-deep ul {
      padding-left: 3px;

      @include bp-mf-tablet {
        padding-left: 0;
      }

      li {
        &::before {
          position: absolute;
          content: '';
          display: inline-block;
          width: 8px;
          height: 8px;
          background: $blue-dark;
          border-radius: 4px;
          left: 0;
          top: 8px;
        }

        position: relative;
        list-style: none;
        margin-bottom: 24px;
        padding-left: 32px;

        @include bp-desktop {
          margin-bottom: 24px;
          padding-left: 48px;
        }
      }
    }
  }

  .img-wrapper {
    @include bp-mf-tablet {
      width: 50%;
      text-align: right;
    }

    @include bp-mf-desktop {
      width: auto;
    }
  }

  .img-container {
    height: 180px;
    overflow: hidden;
    margin-left: -16px;
    margin-right: -16px;
    margin-bottom: 40px;

    @include bp-mf-tablet {
      margin-left: 0;
      margin-right: 0;
      margin-bottom: 64px;

      text-align: right;
      height: auto;
      width: 100%;
    }

    @include bp-mf-desktop {
      width: 540px;
    }

    img {
      width: 100%;
      margin: 0 auto;
    }
  }
</style>
